/** @jsxImportSource @emotion/react */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBubble from '@design-system/text-bubble';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode, IConversationData, ISourceConversationData, ISourceConversationSpeaker } from '@datatype/iyagigo/api-type';
import { TClozeConversationPhase } from '@datatype/iyagigo/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { usePlaySound } from '@hook/common/useSound';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { addFurigana, createFuriganaMap } from '@util/string';
import LineControl from '@component/iyagigo/cloze/conversation-study/conversation-board/line-control';
import Gap from '@component/common/gap';

interface IProps {
  sourceConversation: ISourceConversationData;
  conversation: IConversationData;
  conversationAudioLinks: string[] | null;
  keyword: string;
  isPending: boolean;
  onTypingCompleted: () => void;
}

// ############### helpers ###############
const getIsMyLine = (speakerId: string) => speakerId === 'B';

const getProfileEmoji = (gender: string): string => {
  if (gender === '남' || gender === 'male') return '🧑🏻';
  if (gender === '여' || gender === 'female') return '👩🏻';
  return '🙂';
};

const toFurigana = (text: string, pronunciation: string, isJapanese: boolean) =>
  isJapanese && pronunciation ? addFurigana(text, createFuriganaMap(text, pronunciation), 'start') : text;

const highlightKeyword = (html: string, keyword: string) =>
  keyword ? html.replace(keyword, `<strong style="color: ${color.BROWN_1}">${keyword}</strong>`) : html;

// ############### styled ###############
const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  padding: ${size.MARGIN_1};
  gap: ${size.MARGIN_2};
`;

// ############### component ###############
const ConversationBoard: React.FC<IProps> = ({
  sourceConversation,
  conversation,
  conversationAudioLinks,
  keyword,
  isPending,
  onTypingCompleted,
}) => {
  const dispatch = useTypedDispatch();
  const playSound = usePlaySound(0.3);
  const stopTypingRef = useRef<(() => void) | null>(null);
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;
  const [expandedLineIdx, setExpandedLineIdx] = useState<number | null>(null);

  const toggleExplanation = (idx: number) => {
    setExpandedLineIdx((prev) => (prev === idx ? null : idx));
  };

  const lineCursor = useTypedSelector((state) => state.rootReducer?.workbookReducer?.clozeConversationLineCursor) ?? 0;
  const conversationPhase: TClozeConversationPhase = useTypedSelector(
    (state) => state.rootReducer?.workbookReducer?.clozeConversationPhase,
  ) ?? { type: 'conversation-playing' };

  const sourceLines = sourceConversation.line;
  const speakers = sourceConversation.speaker;
  const keyLineIndex = sourceConversation.keyLine?.index;
  const currentProblemLineIndex =
    conversationPhase.type === 'conversation-problem-choice'
      ? sourceConversation.problem[conversationPhase.problemIndex]?.lineIndex
      : null;

  const getSpeaker = (id: string) => speakers.find((s: ISourceConversationSpeaker) => s.id === id);

  // 새 라인 등장 / phase 변화 시 자동 스크롤. 해설 토글은 제외(현재 위치 유지).
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [lineCursor, conversationPhase.type, isPending]);

  // 현재 라인이 typing 중일 때만 키보드 사운드 루프 재생
  const isTyping = conversationPhase.type === 'conversation-playing' && !isPending && lineCursor < sourceLines.length;
  useEffect(() => {
    // if (isTyping) {
    //   stopTypingRef.current = playSound('typing', { loop: true });
    // } else {
    //   stopTypingRef.current?.();
    //   stopTypingRef.current = null;
    // }
  }, [isTyping, playSound]);

  // 현재 라인(lineCursor)이 problem-line이면 자동으로 problem phase 전환 (사용자 클릭 대기 중일 때만)
  useEffect(() => {
    if (conversationPhase.type !== 'conversation-playing') return;
    if (!isPending) return;
    if (lineCursor >= sourceLines.length) return;
    const problemIndex = sourceConversation.problem.findIndex((p) => p.lineIndex === lineCursor);
    if (problemIndex === -1) return;
    dispatch(workbookActions.storeClozeConversationPhase({ type: 'conversation-problem-choice', problemIndex }));
  }, [conversationPhase.type, isPending, lineCursor, sourceConversation.problem, sourceLines.length, dispatch]);

  // 렌더 대상: idx 0 ~ lineCursor (lineCursor가 현재 활성 라인 인덱스 = thinking/typing 라인).
  // lineCursor === lines.length면 모든 라인 plain 상태 (다음 effect에서 done phase 전환).
  const Lines = sourceLines.slice(0, lineCursor + 1).map((line, idx) => {
    const speaker = getSpeaker(line.speaker);
    const isMyLine = getIsMyLine(line.speaker);
    const isCurrentLine = idx === lineCursor;
    const isProblemLine = idx === currentProblemLineIndex;
    const isKeyLine = idx === keyLineIndex;
    const speakerName = speaker?.name ?? '';
    const profileEmoji = getProfileEmoji(speaker?.gender ?? '');

    // 현재 라인은 isPending에 따라 thinking(클릭 대기)/typing(진행 중), problem 라인은 항상 thinking, 그 외는 plain
    const bubbleType: 'plain' | 'thinking' | 'typing' = isCurrentLine
      ? isPending
        ? 'thinking'
        : 'typing'
      : isProblemLine
        ? 'thinking'
        : 'plain';

    const displayText =
      bubbleType === 'thinking'
        ? ''
        : bubbleType === 'typing'
          ? line.sentence
          : isKeyLine
            ? highlightKeyword(toFurigana(line.sentence, line.pronunciation, isJapanese), keyword)
            : toFurigana(line.sentence, line.pronunciation, isJapanese);

    const convLine = conversation.line[idx];
    const isExpanded = expandedLineIdx === idx;
    const audioLink = conversationAudioLinks?.[idx] ?? null;
    const Control =
      bubbleType === 'thinking' ? undefined : (
        <LineControl
          line={convLine}
          audioLink={audioLink}
          isExpanded={isExpanded}
          isMyLine={isMyLine}
          handleExplanationClick={() => toggleExplanation(idx)}
        />
      );

    return (
      <TextBubble
        key={idx}
        type={bubbleType}
        text={displayText}
        theme={isMyLine ? ETheme.BROWN : ETheme.GRAY}
        align={isMyLine ? 'right' : 'left'}
        speakerName={speakerName}
        profileEmoji={profileEmoji}
        control={Control}
        typingSpeed={isJapanese ? 100 : 50}
        lineMaxWidth='calc(100% - 50px)'
        handleTypingCompleted={isCurrentLine && bubbleType === 'typing' ? onTypingCompleted : undefined}
      />
    );
  });

  return (
    <Wrapper>
      {Lines}
      <div ref={bottomAnchorRef} />
    </Wrapper>
  );
};

export default ConversationBoard;
